// components/gifts/GiftHeroSlider.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LuxuryButton from "../ui/LuxuryButton";
import {
    FaArrowRight,
    FaLeaf,
    FaGem,
    FaStar,
    FaPlay,
    FaGift,
    FaRibbon,
    FaBoxOpen,
    FaHeart
} from 'react-icons/fa';
import {
    GiDiamondRing,
    GiPresent,
    GiCrystalBall,
    GiDiamondHard
} from 'react-icons/gi';

// Gift-themed hero slides data
const giftHeroSlides = [
    {
        id: 1,
        title: "Luxury",
        subtitle: "Gifting",
        description: "Experience the art of extraordinary gift-giving with our meticulously curated collections, where every detail tells a story of luxury and thoughtfulness.",
        stats: [
            { value: "24", label: "Exclusive Collections", icon: <GiDiamondRing className="w-4 h-4" /> },
            { value: "100%", label: "Handcrafted Packaging", icon: <FaBoxOpen className="w-4 h-4" /> },
            { value: "5★", label: "Rated Experience", icon: <FaStar className="w-4 h-4" /> }
        ],
        ctaPrimary: "Explore Collections",
        ctaSecondary: "Gift Consultation",
        badge: "Signature Edition",
        productName: "The KROMAIC Collection",
        productTagline: "Ultimate Luxury",
        rating: "4.9 Rating",
        gradient: "linear-gradient(135deg, #f9f5f2 0%, #f0e9e4 100%)",
        accentColor: "#d4b483",
        productColor: "#9caf88",
        productGradient: "linear-gradient(135deg, #d4b48320 0%, #9caf8820 100%)"
    },
    {
        id: 2,
        title: "Bespoke",
        subtitle: "Personalization",
        highlight: "Perfected",
        description: "Transform your gift into a personal masterpiece with our exclusive monogramming and custom formulation services.",
        stats: [
            { value: "48", label: "Personalization Options", icon: <FaRibbon className="w-4 h-4" /> },
            { value: "100%", label: "Handwritten Notes", icon: <FaHeart className="w-4 h-4" /> },
            { value: "5★", label: "Rated Service", icon: <FaStar className="w-4 h-4" /> }
        ],
        ctaPrimary: "Create Custom Gift",
        ctaSecondary: "View Personalization",
        badge: "Limited Edition",
        productName: "Monogrammed Treasury",
        productTagline: "Custom Collection",
        rating: "4.8 Rating",
        gradient: "linear-gradient(135deg, #E8DFD0 0%, #D8C9B0 100%)",
        accentColor: "#9caf88",
        productColor: "#7A8F8C",
        productGradient: "linear-gradient(135deg, #9caf8820 0%, #7A8F8C20 100%)"
    },
    {
        id: 3,
        title: "Celebration",
        subtitle: "Collections",
        highlight: "Curated",
        description: "Every special moment deserves an extraordinary gift. Our celebration collections are designed for life's most precious occasions.",
        stats: [
            { value: "12", label: "Occasion Themes", icon: <FaGift className="w-4 h-4" /> },
            { value: "White Glove", label: "Delivery Service", icon: <GiPresent className="w-5 h-5" /> },
            { value: "5★", label: "Rated Experience", icon: <FaStar className="w-4 h-4" /> }
        ],
        ctaPrimary: "Browse Occasions",
        ctaSecondary: "Gift Calendar",
        badge: "Celebration Edition",
        productName: "Célébration Royale",
        productTagline: "Festive Collection",
        rating: "4.9 Rating",
        gradient: "linear-gradient(135deg, #F5F0E6 0%, #E8E0D1 100%)",
        accentColor: "#b08d7a",
        productColor: "#d4b483",
        productGradient: "linear-gradient(135deg, #d4b48320 0%, #E8E0D1 100%)"
    }
];

const GiftHeroSlider = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPlaying] = useState(true);
    const [scrollProgress, setScrollProgress] = useState(0);
    const intervalRef = useRef(null);
    const containerRef = useRef(null);

    // Auto-rotate slides every 4 seconds
    useEffect(() => {
        if (!isPlaying) return;

        intervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % giftHeroSlides.length);
        }, 4000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPlaying]);

    // Scroll progress for parallax effect
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            const windowHeight = window.innerHeight;
            const progress = Math.min(scrollTop / (windowHeight * 0.5), 1);
            setScrollProgress(progress);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const currentSlideData = giftHeroSlides[currentSlide];

    // Animation variants
    const slideVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0
        })
    };

    const contentVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    const imageVariants = {
        hidden: {
            opacity: 0,
            scale: 0.9,
            rotate: -5
        },
        visible: {
            opacity: 1,
            scale: 1,
            rotate: 0,
            transition: {
                duration: 1,
                ease: "easeOut"
            }
        }
    };

    return (
        <section
            ref={containerRef}
            className="relative min-h-screen lg:min-h-[90vh] overflow-hidden pt-10 lg:pt-10"
            style={{
                background: currentSlideData.gradient,
            }}
        >
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Floating Gift Particles */}
                {[...Array(12)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute"
                        animate={{
                            y: [0, -40, 0],
                            rotate: [0, 180, 360],
                            scale: [1, 1.2, 1]
                        }}
                        transition={{
                            duration: 8 + i * 0.5,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        style={{
                            left: `${(i * 7) % 100}%`,
                            top: `${(i * 5) % 100}%`,
                        }}
                    >
                        {i % 4 === 0 ? (
                            <FaGift className="w-5 h-5" style={{ color: `${currentSlideData.accentColor}30` }} />
                        ) : i % 4 === 1 ? (
                            <GiDiamondHard className="w-5 h-5" style={{ color: `${currentSlideData.accentColor}30` }} />
                        ) : i % 4 === 2 ? (
                            <FaRibbon className="w-5 h-5" style={{ color: `${currentSlideData.accentColor}20` }} />
                        ) : (
                            <GiCrystalBall className="w-5 h-5" style={{ color: `${currentSlideData.productColor}20` }} />
                        )}
                    </motion.div>
                ))}

                {/* Gradient Orbs */}
                <motion.div
                    className="absolute top-1/4 -left-32 w-96 h-96 rounded-full"
                    style={{
                        background: `radial-gradient(circle, ${currentSlideData.productColor}15 0%, transparent 70%)`,
                        filter: 'blur(40px)',
                    }}
                    animate={{
                        scale: [1, 1.3, 1],
                        x: [0, 40, 0],
                        y: [0, -20, 0],
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />

                <motion.div
                    className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full"
                    style={{
                        background: `radial-gradient(circle, ${currentSlideData.accentColor}10 0%, transparent 70%)`,
                        filter: 'blur(30px)',
                    }}
                    animate={{
                        scale: [1, 1.4, 1],
                        x: [0, -30, 0],
                        y: [0, 20, 0],
                    }}
                    transition={{
                        duration: 18,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1,
                    }}
                />

                {/* Gift Pattern */}
                <div
                    className="absolute inset-0 opacity-[0.02]"
                    style={{
                        backgroundImage: `
              radial-gradient(circle at 25px 25px, ${currentSlideData.accentColor}20 2px, transparent 2px),
              radial-gradient(circle at 75px 75px, ${currentSlideData.productColor}20 2px, transparent 2px)
            `,
                        backgroundSize: '100px 100px'
                    }}
                />
            </div>

            {/* Slide Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentSlide}
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.4 }
                    }}
                    className="container-luxury relative z-10 h-screen flex items-center"
                >
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
                        {/* Left Column - Text Content */}
                        <div>
                            <motion.div
                                variants={contentVariants}
                                initial="hidden"
                                animate="visible"
                                className="space-y-6"
                            >
                                {/* Premium Badge */}
                                <div className="mb-4">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-champagne-gold/20 to-muted-sage/20 backdrop-blur-sm rounded-full border border-champagne-gold/30">
                                        <GiDiamondHard className="w-4 h-4 text-champagne-gold animate-pulse" />
                                        <span className="text-xs tracking-widest uppercase text-champagne-gold font-medium">
                      {currentSlideData.badge}
                    </span>
                                        <div className="w-1 h-1 bg-champagne-gold rounded-full animate-ping" />
                                    </div>
                                </div>

                                {/* Main Headline */}
                                <div className="space-y-3 mb-4">
                                    <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-playfair font-light leading-[0.9]">
                                        {currentSlideData.title}
                                        <span className="block text-muted-sage italic mt-2">
                      {currentSlideData.subtitle}
                    </span>
                                    </h1>
                                    <div className="w-24 h-px bg-gradient-to-r from-champagne-gold to-transparent" />
                                </div>

                                {/* Subtitle */}
                                <p className="text-2xl md:text-3xl text-charcoal-60 leading-relaxed mb-6">
                                    {currentSlideData.description}
                                </p>

                                {/* Stats with Icons */}
                                <div className="flex flex-wrap gap-6 mb-8">
                                    {currentSlideData.stats.map((stat, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 + index * 0.1 }}
                                            className="text-center group"
                                        >
                                            <div className="flex items-center justify-center gap-2 mb-1">
                                                <div className="text-muted-sage">
                                                    {stat.icon}
                                                </div>
                                                <div
                                                    className="text-3xl font-light"
                                                    style={{ color: currentSlideData.accentColor }}
                                                >
                                                    {stat.value}
                                                </div>
                                            </div>
                                            <div className="text-sm text-charcoal-40 uppercase tracking-wider group-hover:text-charcoal-60 transition-colors">
                                                {stat.label}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* CTA Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                                    <LuxuryButton
                                        variant="primary"
                                        size="large"
                                        onClick={() => {
                                            document.getElementById('products')?.scrollIntoView({
                                                behavior: 'smooth'
                                            });
                                        }}
                                        className="group"
                                        icon={FaArrowRight}
                                        iconPosition="right"
                                    >
                                        <span>{currentSlideData.ctaPrimary}</span>
                                    </LuxuryButton>

                                    <LuxuryButton
                                        variant="secondary"
                                        size="large"
                                        onClick={() => {
                                            console.log('Watch story');
                                        }}
                                        icon={FaPlay}
                                        iconPosition="left"
                                    >
                                        <span className="hidden sm:inline">{currentSlideData.ctaSecondary}</span>
                                        <span className="sm:hidden">Story</span>
                                    </LuxuryButton>
                                </div>

                                {/* Luxury Indicators */}
                                <div className="flex flex-wrap items-center gap-4 text-sm text-charcoal-40">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-muted-sage rounded-full animate-pulse" />
                                        <span>White Glove Delivery</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-champagne-gold rounded-full animate-pulse" />
                                        <span>Personal Monogramming</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-deep-charcoal/40 rounded-full animate-pulse" />
                                        <span>Global Shipping</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Right Column - Gift Visual */}
                        <motion.div
                            variants={imageVariants}
                            initial="hidden"
                            animate="visible"
                            className="relative hidden lg:block"
                        >
                            {/* Gift Container */}
                            <div className="relative aspect-square rounded-3xl overflow-hidden">
                                <div
                                    className="absolute inset-0"
                                    style={{ background: currentSlideData.productGradient }}
                                />

                                {/* Floating Gift Box */}
                                <motion.div
                                    className="absolute inset-8 bg-gradient-to-br from-white to-pearl-white rounded-2xl shadow-lift"
                                    animate={{
                                        y: [0, -20, 0],
                                        rotate: [0, 1, -1, 0]
                                    }}
                                    transition={{
                                        duration: 8,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                >
                                    {/* Gift Label */}
                                    <div className="absolute top-8 left-8 right-8">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-xs tracking-widest uppercase text-muted-sage mb-1">
                                                    {currentSlideData.badge}
                                                </div>
                                                <div className="text-xl font-cormorant text-deep-charcoal">
                                                    {currentSlideData.productName}
                                                </div>
                                            </div>
                                            <div
                                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                                style={{
                                                    background: `linear-gradient(135deg, ${currentSlideData.productColor}, ${currentSlideData.accentColor})`
                                                }}
                                            >
                                                <GiDiamondHard className="w-5 h-5 text-white" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Gift Box Preview */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="relative">
                                            {/* Gift Box */}
                                            <div className="relative w-48 h-48">
                                                {/* Box Base */}
                                                <div className="absolute inset-0 bg-gradient-to-b from-white via-pearl-white to-nude-beige rounded-lg shadow-inner" />

                                                {/* Ribbon */}
                                                <motion.div
                                                    className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-full"
                                                    style={{ backgroundColor: currentSlideData.accentColor }}
                                                    animate={{
                                                        scaleY: [1, 1.1, 1],
                                                        opacity: [0.8, 1, 0.8]
                                                    }}
                                                    transition={{
                                                        duration: 2,
                                                        repeat: Infinity,
                                                        ease: "easeInOut"
                                                    }}
                                                />

                                                <motion.div
                                                    className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1"
                                                    style={{ backgroundColor: currentSlideData.accentColor }}
                                                    animate={{
                                                        scaleX: [1, 1.1, 1],
                                                        opacity: [0.8, 1, 0.8]
                                                    }}
                                                    transition={{
                                                        duration: 2,
                                                        repeat: Infinity,
                                                        ease: "easeInOut",
                                                        delay: 0.5
                                                    }}
                                                />

                                                {/* Bow */}
                                                <motion.div
                                                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                                                    animate={{
                                                        rotate: [0, 5, -5, 0]
                                                    }}
                                                    transition={{
                                                        duration: 3,
                                                        repeat: Infinity,
                                                        ease: "easeInOut"
                                                    }}
                                                >
                                                    <div className="relative">
                                                        <div className="w-8 h-8" style={{ backgroundColor: currentSlideData.productColor }} />
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <GiDiamondHard className="w-4 h-4 text-white" />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Floating Diamond Particles */}
                                    {[...Array(6)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            className="absolute w-3 h-3"
                                            style={{
                                                background: `linear-gradient(to right, ${currentSlideData.productColor}40, ${currentSlideData.accentColor}40)`,
                                                left: `${20 + (i * 15)}%`,
                                                bottom: '20%',
                                                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
                                            }}
                                            animate={{
                                                y: [0, -40, 0],
                                                x: [0, Math.sin(i) * 15, 0],
                                                rotate: [0, 180, 360],
                                                opacity: [0.3, 0.9, 0.3]
                                            }}
                                            transition={{
                                                duration: 4 + i * 0.5,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                        />
                                    ))}
                                </motion.div>

                                {/* Decorative Border */}
                                <motion.div
                                    className="absolute -inset-1 border-2 border-transparent rounded-3xl"
                                    animate={{
                                        borderColor: [
                                            `${currentSlideData.productColor}20`,
                                            `${currentSlideData.accentColor}40`,
                                            `${currentSlideData.productColor}20`
                                        ]
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                />
                            </div>

                            {/* Edition Badge */}
                            <motion.div
                                className="absolute -top-4 -right-4 px-6 py-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lift flex items-center gap-3"
                                animate={{
                                    scale: [1, 1.05, 1],
                                    rotate: [0, 2, -2, 0]
                                }}
                                transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                <div
                                    className="w-3 h-3 rounded-full animate-pulse"
                                    style={{ backgroundColor: currentSlideData.productColor }}
                                />
                                <span className="text-sm font-poppins font-medium text-deep-charcoal">
                  {currentSlideData.productTagline}
                </span>
                            </motion.div>

                            {/* Rating Badge */}
                            <motion.div
                                className="absolute -bottom-4 left-8 px-4 py-2 text-white rounded-full flex items-center gap-2 shadow-lift"
                                style={{
                                    background: `linear-gradient(to right, ${currentSlideData.productColor}, ${currentSlideData.accentColor})`
                                }}
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.8 }}
                            >
                                <FaStar className="w-3 h-3" />
                                <span className="text-sm font-poppins font-medium">{currentSlideData.rating}</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Slide Indicators */}
            <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                {giftHeroSlides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`relative w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentSlide
                                ? 'w-8 bg-champagne-gold'
                                : 'bg-deep-charcoal/30 hover:bg-deep-charcoal/50'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                    >
                        {index === currentSlide && (
                            <motion.div
                                className="absolute inset-0 rounded-full bg-champagne-gold/20"
                                animate={{
                                    scale: [1, 1.5, 1],
                                    opacity: [0.5, 0, 0.5]
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            />
                        )}
                    </button>
                ))}
            </div>

            {/* Scroll Indicator */}
            <motion.div
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer z-20"
                animate={{
                    y: [0, 10, 0],
                    opacity: [0.3, 0.8, 0.3]
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
                onClick={() => {
                    document.getElementById('collections-nav')?.scrollIntoView({
                        behavior: 'smooth'
                    });
                }}
            >
                <div className="flex flex-col items-center gap-2">
          <span className="text-xs tracking-widest uppercase text-charcoal-30 font-poppins">
            Explore Collections
          </span>
                    <div className="relative">
                        <div
                            className="w-px h-16 bg-gradient-to-b from-champagne-gold via-muted-sage to-transparent"
                            style={{
                                background: `linear-gradient(to bottom, ${currentSlideData.productColor}, ${currentSlideData.accentColor}, transparent)`
                            }}
                        />
                        <motion.div
                            className="absolute top-0 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full"
                            style={{ backgroundColor: currentSlideData.productColor }}
                            animate={{ y: [0, 60, 0] }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                    </div>
                </div>
            </motion.div>

            {/* Parallax Brand Text */}
            <motion.div
                className="absolute bottom-20 left-0 right-0 pointer-events-none overflow-hidden z-0"
                style={{ y: scrollProgress * 100 }}
            >
                <div className="container-luxury">
                    <div className="text-center">
            <span className="text-6xl md:text-8xl font-playfair font-bold text-white/10 tracking-wider">
              GIFTING PERFECTED
            </span>
                    </div>
                </div>
            </motion.div>
        </section>
    );
};

export default GiftHeroSlider;